import { useEffect, useRef } from 'react';
import { useMap } from './useMap';
import { OVERVIEW_CAMERA } from './MapContainer';

interface SelectableLocation {
  lng: number;
  lat: number;
  cameraTarget?: { zoom?: number; pitch?: number; bearing?: number };
}

type Padding = { top: number; bottom: number; left: number; right: number };

/**
 * Phone and tablet pages mark the clear strip of map between their cards
 * with `data-map-window`. Framing the location in that strip keeps it out
 * from behind the cards; the default narrow padding centred it about 58%
 * down the map, under the earnings tiles (Warren, 20 October 2026).
 *
 * Measured where the strip sits with the page scrolled to the top, so the
 * answer does not depend on when it is asked. Null when no strip is
 * displayed (desktop), the map is not narrow, or too little of the strip
 * falls on the map to frame anything in.
 */
function mapWindowPadding(container: HTMLElement): Padding | null {
  if (container.clientWidth >= 1024) return null;
  const strip = document.querySelector<HTMLElement>('[data-map-window]');
  if (!strip || strip.offsetParent === null) return null;
  const map = container.getBoundingClientRect();
  const win = strip.getBoundingClientRect();
  const top = Math.max(0, win.top + window.scrollY - map.top);
  const bottom = Math.max(0, map.bottom - Math.min(win.bottom + window.scrollY, map.bottom));
  if (map.height - top - bottom < 120) return null;
  return { top, bottom, left: 24, right: 24 };
}

/** Camera changes follow selection/coordinates, never refreshed count objects. */
export function CameraController({ selected, points = [], siteView = false, ready = true }: {
  selected: SelectableLocation | null;
  points?: ReadonlyArray<SelectableLocation>;
  siteView?: boolean;
  ready?: boolean;
}) {
  const { map, isLoaded } = useMap();
  const applied = useRef<{ map: typeof map; key: string } | null>(null);
  const stripInUse = useRef(false);
  const coordinates = JSON.stringify(points.map((p) => [p.lng, p.lat]).sort((a, b) => a[0]! - b[0]! || a[1]! - b[1]!));
  const lng = selected?.lng;
  const lat = selected?.lat;
  const zoom = selected?.cameraTarget?.zoom ?? 14;
  const pitch = selected?.cameraTarget?.pitch ?? (siteView ? 0 : 60);
  const bearing = selected?.cameraTarget?.bearing ?? (siteView ? 0 : -20);

  useEffect(() => {
    if (!map || !isLoaded || !ready) return;
    const key = JSON.stringify([lng, lat, zoom, pitch, bearing, siteView, siteView ? coordinates : '']);
    if (applied.current?.map === map && applied.current.key === key) return;
    const coords = JSON.parse(coordinates) as Array<[number, number]>;
    if (siteView && coords.length === 0) return;
    applied.current = { map, key };
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduced ? 0 : 1000;
    const width = map.getContainer().clientWidth;
    const height = map.getContainer().clientHeight;
    const narrow = width < 1024;
    const strip = narrow ? mapWindowPadding(map.getContainer()) : null;
    const padding = strip ?? (narrow
      ? { top: Math.min(280, height * 0.4), bottom: Math.max(60, height * 0.2), left: 50, right: 50 }
      : { top: 70, bottom: Math.min(260, height * 0.3), left: Math.round(width * 0.36), right: lng !== undefined ? Math.min(380, width * 0.28) : 80 });
    // flyTo keeps its padding on the map afterwards; fitBounds does not. Only a flight that left the strip's padding
    // in place is kept in step with the strip below.
    stripInUse.current = false;
    if (lng !== undefined && lat !== undefined) {
      map.flyTo({ center: [lng, lat], zoom, pitch, bearing, duration, essential: false, padding: siteView ? padding : 0 });
      stripInUse.current = siteView && strip !== null;
    } else if (siteView) {
      if (coords.length === 1) {
        map.flyTo({ center: coords[0], zoom: 14, pitch: 0, bearing: 0, padding, duration, essential: false });
        stripInUse.current = strip !== null;
      } else {
        const xs = coords.map((p) => p[0]);
        const ys = coords.map((p) => p[1]);
        map.fitBounds([[Math.min(...xs), Math.min(...ys)], [Math.max(...xs), Math.max(...ys)]], {
          padding, maxZoom: 15, pitch: 0, bearing: 0, duration, essential: false,
        });
      }
    } else {
      map.flyTo({ center: [OVERVIEW_CAMERA.lng, OVERVIEW_CAMERA.lat], zoom: OVERVIEW_CAMERA.zoom,
        pitch: OVERVIEW_CAMERA.pitch, bearing: OVERVIEW_CAMERA.bearing, duration, essential: false, padding: 0 });
    }
  }, [map, isLoaded, ready, lng, lat, zoom, pitch, bearing, siteView, coordinates]);

  // The strip moves as the page lays out: the payout banner can arrive after the flight, pushing every card down.
  // Keep the map's padding on the strip so the framed location moves with it rather than sliding under a card.
  // setPadding is a jump, which would cut a flight or a drag short, so it waits for the camera to stop.
  useEffect(() => {
    if (!map || !isLoaded) return;
    let pending = false;
    const sync = () => {
      if (!stripInUse.current || pending) return;
      const apply = () => {
        pending = false;
        const p = mapWindowPadding(map.getContainer());
        if (!p || !stripInUse.current) return;
        const now = map.getPadding();
        if (Math.abs((now.top ?? 0) - p.top) < 4 && Math.abs((now.bottom ?? 0) - p.bottom) < 4) return;
        map.setPadding(p);
      };
      if (map.isMoving()) { pending = true; map.once('moveend', apply); } else apply();
    };
    const observer = new ResizeObserver(sync);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [map, isLoaded]);

  return null;
}
